import {
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { WhatsApp } from "@mui/icons-material";
import { mapFhirPatientToMessagingPatient } from "@/pages/adminInsights/utils/mapConsultationToWhatsApp";
import type { MessagingPatient } from "src-next/modules/crm-clinical/messaging/types";
import { READABLE_BODY_SX } from "src-next/modules/clinical-insights/constants/clinicalInsightsTheme";
import {
  statsTableCellTextSx,
  statsTableHeadCellSx,
} from "src-next/modules/clinical-insights/helpers/insightPatientsTableStyles";
import type { ConsentNotAttendedRow } from "src-next/modules/clinical-insights/hooks/useConsentNotAttendedPatients";
import { patientHasPhone } from "src-next/modules/clinical-insights/components/workspace/patients/insightPatientsUtils";

const NO_PHONE_TITLE = "Paciente sem telefone cadastrado";

const patientNameSx = {
  ...statsTableCellTextSx,
  ...READABLE_BODY_SX,
  fontWeight: 600,
} as const;

interface ConsentNotAttendedTableProps {
  pageRows: ConsentNotAttendedRow[];
  selectedIds: Set<string>;
  allPageSelected: boolean;
  somePageSelected: boolean;
  whatsAppDisabled: boolean;
  whatsAppTooltip: string;
  onTogglePage: () => void;
  onToggleOne: (patientId: string) => void;
  onWhatsApp: (patient: MessagingPatient) => void;
}

export function ConsentNotAttendedTable({
  pageRows,
  selectedIds,
  allPageSelected,
  somePageSelected,
  whatsAppDisabled,
  whatsAppTooltip,
  onTogglePage,
  onToggleOne,
  onWhatsApp,
}: ConsentNotAttendedTableProps) {
  return (
    <TableContainer>
      <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" sx={statsTableHeadCellSx}>
              <Checkbox
                size="small"
                checked={allPageSelected}
                indeterminate={!allPageSelected && somePageSelected}
                onChange={onTogglePage}
                inputProps={{ "aria-label": "Selecionar todos da página" }}
              />
            </TableCell>
            <TableCell sx={statsTableHeadCellSx}>Paciente</TableCell>
            <TableCell sx={{ ...statsTableHeadCellSx, width: 88 }} align="right">
              Ações
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pageRows.map((row) => {
            const phoneOk = patientHasPhone(row.patient);
            const rowDisabled = whatsAppDisabled || !phoneOk;
            const rowTitle = !phoneOk ? NO_PHONE_TITLE : whatsAppTooltip;
            return (
              <TableRow key={row.patientId} hover selected={selectedIds.has(row.patientId)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(row.patientId)}
                    onChange={() => onToggleOne(row.patientId)}
                    inputProps={{ "aria-label": `Selecionar ${row.name}` }}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 0 }}>
                  <Typography title={row.name} noWrap sx={patientNameSx}>
                    {row.name}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={rowTitle}>
                    <span>
                      <IconButton
                        size="medium"
                        color="success"
                        disabled={rowDisabled}
                        aria-label={`WhatsApp para ${row.name}`}
                        onClick={() => {
                          const patient = mapFhirPatientToMessagingPatient(row.patient);
                          if (patient) onWhatsApp(patient);
                        }}
                      >
                        <WhatsApp />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
